import {createSelector} from 'reselect';
import {currencyFormatter} from '@homeaway/hacommons-i18n';

import {getTranslations} from './store';
import {getCurrency, getLocale} from './system';
import {getRateDetails, getRateSummary, getTaxRate} from './listing';
import get from 'lodash/get';
/**
 * Filter out empty strings from the rateDetails' notes.  In some cases,
 * the notes will contain only empty strings, so we need to do this in order to
 * decide whether or not to show the rateDetails
 *
 * @param rateDetails
 * @return rateDetails, with empty notes filtered out
 */
const filterEmptyRateDetails = (rateDetails) => {
    if (!rateDetails.notes) {
        return rateDetails;
    }

    return Object.assign({}, rateDetails, {
        notes: rateDetails.notes.filter((detail) => {
            return detail.length > 0;
        })
    });
};

const formatFlatFee = (min, max, currencyCode, locale) => {
    const localizedMin = currencyFormatter.formatNumber({value: min, currencyCode, locale});
    const localizedMax = currencyFormatter.formatNumber({value: max, currencyCode, locale});

    if (min === max || !max) {
        return localizedMin;
    }

    return `${localizedMin} - ${localizedMax}`;
};

const formatPercentageFee = (min, max) => {
    if (min === max) {
        return `${min}%`;
    }

    return `${min}% - ${max}%`;
};

const compileFeesList = (rateSummary, refundableDamageDeposit, taxRate, currencyCode, locale) => {
    let fees = [];
    let flatFees = null;
    let percentageFees = null;
    const loggedTypes = [];

    if (rateSummary && rateSummary.flatFees) {
        flatFees = rateSummary.flatFees.map((fee) => {
            const feeType = fee.type;

            if (loggedTypes.indexOf(feeType) > -1) {
                // fee has already been logged as part of consolidated fee types
                return null;
            }

            loggedTypes.push(feeType);

            const allFeeAmounts = [];

            rateSummary.flatFees.forEach((feeOfType) => {
                if (feeOfType.type === feeType) {
                    allFeeAmounts.push(feeOfType.minAmountConverted);
                    allFeeAmounts.push(feeOfType.maxAmountConverted);
                }
            });

            return {
                description: fee.description,
                amount: formatFlatFee(
                    Math.min(...allFeeAmounts),
                    Math.max(...allFeeAmounts),
                    currencyCode,
                    locale
                )
            };
        });

        flatFees = flatFees.filter(Boolean);
        fees = fees.concat(flatFees);
    }

    if (rateSummary && rateSummary.percentageFees) {
        percentageFees = rateSummary.percentageFees.map((fee) => {
            return {
                description: fee.description,
                amount: formatPercentageFee(fee.minAmount, fee.maxAmount)
            };
        });
        fees = fees.concat(percentageFees);
    }

    if (refundableDamageDeposit) {
        const rdd = {
            description: refundableDamageDeposit.description,
            amount: formatFlatFee(
                refundableDamageDeposit.minAmountConverted,
                refundableDamageDeposit.maxAmountConverted,
                currencyCode,
                locale
            )
        };

        fees = fees.concat([rdd]);
    }

    if (taxRate) {
        fees = fees.concat([taxRate]);
    }

    return fees;
};

/* eslint-disable complexity */
const compileFeesListFromRateDetails = (rateDetails) => {
    const {rateSections = []} = rateDetails || {};
    if (rateSections.length > 0) {
        const feesSection = rateSections.find((section) => section.type === 'FEES');
        const {rateInfos = []} = feesSection || {};
        if (rateInfos.length > 0) {
            const additionalCosts = rateInfos.filter(
                (info) => info.type === 'ADDITIONAL_COST' && info.title && info.details
            );
            const fees = additionalCosts.map(({title, details}) => ({
                description: title,
                amount: details
            }));
            if (fees.length > 0) {
                return fees;
            }
        }
    }
    return null;
};
/* eslint-enable complexity */

const getRefundableDamageDeposit = createSelector(
    [getRateSummary],
    (rateSummary) => {
        return rateSummary.refundableDamageDeposit || null;
    }
);

const getTaxRateObj = createSelector(
    [getTranslations, getTaxRate],
    (trans, taxRate) => {
        if (taxRate) {
            return {
                description: trans.taxRate,
                amount: taxRate
            };
        }
        return null;
    }
);

const getFees = createSelector(
    [
        getRefundableDamageDeposit,
        getTaxRateObj,
        getCurrency,
        getLocale,
        getRateSummary,
        getRateDetails
    ],
    (refundableDamageDeposit, taxRateObj, currency, locale, rateSummary, rateDetails) => {
        if (!rateSummary.accurateRate) {
            return compileFeesListFromRateDetails(rateDetails);
        }
        let fees = compileFeesList(rateSummary, refundableDamageDeposit, null, currency, locale);

        return fees.length && fees;
    }
);

const getRateDetailsData = createSelector(
    [getFees, getRateDetails, getRateSummary, getTranslations],
    (fees, rateDetails, rateSummary, trans) => {
        const filteredRateDetails = filterEmptyRateDetails(rateDetails);
        const showRateDetails =
            !!(filteredRateDetails.notes && filteredRateDetails.notes.length) ||
            !!(fees && fees.length);
        const labels = {
            header: trans.rateDetailsHeader,
            paidAtPropertyHeader: trans.paidAtPropertyHeader,
            usageBasedFeesLabel: trans.usageBasedFeesLabel,
            paidAtPropertyDisclaimer: trans.paidAtPropertyDisclaimer,
            optionalFeesHeader: trans.optionalFeesHeader,
            optionalFeesDisclaimer: trans.optionalFeesDisclaimer
        };

        const paidAtPropertyFees = get(
            rateSummary,
            'stayCollectedFeeSummary.mandatoryFlatFees',
            []
        );
        const usageBasedFees = get(rateSummary, 'stayCollectedFeeSummary.meteredFees', []);
        const optionalFees = get(rateSummary, 'stayCollectedFeeSummary.optionalFlatFees', []);
        return {
            rateDetails: filteredRateDetails,
            fees: fees === null ? [] : fees,
            usageBasedFees: usageBasedFees === null ? [] : usageBasedFees,
            paidAtPropertyFees: paidAtPropertyFees === null ? [] : paidAtPropertyFees,
            optionalFees: optionalFees === null ? [] : optionalFees,
            showRateDetails,
            labels
        };
    }
);

export default getRateDetailsData;
export {
    compileFeesList,
    compileFeesListFromRateDetails,
    formatFlatFee,
    formatPercentageFee,
    getFees,
    getRateDetailsData,
    getRefundableDamageDeposit,
    getTaxRateObj
};
